import type { ReactNode } from "react"

/**
 * Generic dashboard engine types. This module is intentionally domain-agnostic — it must NOT know
 * anything about Finance (or any other feature). Features supply widgets via the registry; the engine
 * understands a single ordered list of widgets, each occupying 1–3 column tracks of a responsive grid.
 */

/** How many column tracks a widget occupies on the widest (3-column) breakpoint. */
export type DashboardWidgetSpan = 1 | 2 | 3

export const MAX_DASHBOARD_SPAN: DashboardWidgetSpan = 3

/** Clamp an arbitrary number to a valid span within a widget's own maximum. */
export function clampSpan(value: number, maxSpan: DashboardWidgetSpan): DashboardWidgetSpan {
  const bounded = Math.max(1, Math.min(Math.round(value), maxSpan))
  return bounded as DashboardWidgetSpan
}

/** Loading / async lifecycle of a single widget's content. Drives the shell's rendered state. */
export enum DashboardWidgetState {
  Loading = "loading",
  Error = "error",
  Empty = "empty",
  Ready = "ready",
}

/**
 * A placed widget instance's chrome (title/subtitle/icon). Placement/order lives in `DashboardLayout.order`
 * and the width in `DashboardLayout.spans`; a widget known to `widgets` but absent from `order` is hidden.
 */
export interface DashboardWidget {
  id: string
  title: string
  subtitle?: string
  icon?: ReactNode
}

/**
 * The full layout state. `order` (a single ordered list of visible widget IDs) is the source of truth for
 * placement; `spans` holds each widget's current column span; a widget in `widgets` but not in `order` is
 * hidden.
 */
export interface DashboardLayout {
  order: string[]
  widgets: Record<string, DashboardWidget>
  spans: Record<string, DashboardWidgetSpan>
}

/**
 * A registry entry describing how to render a widget's inner content. Features register one definition
 * per widget ID. The engine calls `render()` and drops the result inside the reusable widget shell.
 */
export interface DashboardWidgetDefinition {
  id: string
  title: string
  subtitle?: string
  icon?: ReactNode
  /** Largest span this widget may be resized to (1 = never wider than one column). Defaults to 1. */
  maxSpan?: DashboardWidgetSpan
  /** Opt-in: show an expand control that opens the widget full-size in a dialog (for charts/dense content). */
  expandable?: boolean
  /** Defaults to {@link DashboardWidgetChrome.Standard}. */
  chrome?: DashboardWidgetChrome
  render: () => ReactNode
}

/** Who draws a widget's card. */
export enum DashboardWidgetChrome {
  /** The engine's shell: card surface, icon + title header, menu, padded body. The default. */
  Standard = "standard",
  /**
   * The widget draws its own card (surface, header, body). The engine still owns placement and every edit-mode
   * control (drag surface, hide, resize), so a self-drawn tile is exactly as configurable as a standard one.
   */
  None = "none",
}

/** Map of widget ID -> its render definition. */
export type DashboardWidgetRegistry = Record<string, DashboardWidgetDefinition>
